"use client"
import styled from "@emotion/styled"
import { useEffect, useMemo, useRef, useState } from "react"

// Helpers
function getUnitLabels(locale: string) {
  const isHe = locale?.toLowerCase().startsWith("he")
  const enPlural = new Intl.PluralRules("en")
  return {
    format(value: number, unit: "year" | "month" | "day", numFmt?: Intl.NumberFormat) {
      const n = Math.max(0, Math.trunc(value))
      const f = numFmt ?? new Intl.NumberFormat(locale, { useGrouping: false })
      const num = f.format(n)
      if (isHe) {
        // Basic Hebrew singular/plural
        const word =
          unit === "year"
            ? n === 1
              ? "שנה"
              : "שנים"
            : unit === "month"
            ? n === 1
              ? "חודש"
              : "חודשים"
            : n === 1
            ? "יום"
            : "ימים"
        return `${num} ${word}`
      }
      // English
      const category = enPlural.select(n)
      const word =
        unit === "year"
          ? category === "one"
            ? "year"
            : "years"
          : unit === "month"
          ? category === "one"
            ? "month"
            : "months"
          : category === "one"
          ? "day"
          : "days"
      return `${num} ${word}`
    },
  }
}

const StyledStrong = styled.strong`
  /* width: ; */
`

const ClockDisplay = styled.span`
  /* Add your clock-specific styles here */
  width: 7ch;
  display: inline-flex;
  justify-content: center;
  ${(props) => props.theme.breakpoints.up("lg")} {
    font-size: 40px;
  }
`

const UnitsDisplay = styled.span`
  /* Add your units-specific styles here */
  font-size: 14px;
  font-weight: 300;
  ${(props) => props.theme.breakpoints.up("lg")} {
    font-size: 24px;
    margin-inline-end: 10px;
  }
`

type CountdownTimerProps = {
  target: string | number | Date | null | undefined
  locale?: string
  showDays?: boolean
  showMonthsYears?: boolean
  onEnd?: () => void
  className?: string
}

// Renders separate elements for units (years, months, days) and clock (HH:MM:SS)
export default function CountdownTimer({
  target,
  locale = "he",
  showDays = false,
  showMonthsYears = false,
  onEnd,
  className,
}: CountdownTimerProps) {
  const [clockDisplay, setClockDisplay] = useState<string>("00:00:00")
  const [unitsDisplay, setUnitsDisplay] = useState<string>("")
  const endedRef = useRef(false)

  const targetMs = useMemo(() => {
    if (!target) return NaN
    const t = target instanceof Date ? target.getTime() : new Date(target).getTime()
    return t
  }, [target])

  const twoDigits = useMemo(
    () => new Intl.NumberFormat(locale, { minimumIntegerDigits: 2, useGrouping: false }),
    [locale],
  )
  const intFmt = useMemo(() => new Intl.NumberFormat(locale, { useGrouping: false }), [locale])
  const labels = useMemo(() => getUnitLabels(locale), [locale])

  useEffect(() => {
    if (!targetMs || Number.isNaN(targetMs)) {
      setClockDisplay("00:00:00")
      setUnitsDisplay("")
      return
    }

    const update = () => {
      const now = Date.now()
      let diff = targetMs - now
      if (diff <= 0) {
        diff = 0
        if (!endedRef.current) {
          endedRef.current = true
          onEnd?.()
        }
      }

      const totalSeconds = Math.floor(diff / 1000)

      if (showMonthsYears) {
        // Compute calendar years and months by advancing from "now" without exceeding target
        const nowDate = new Date()
        let cursor = new Date(nowDate)
        const targetDate = new Date(targetMs)

        const addYears = (d: Date, y: number) => {
          const nd = new Date(d)
          nd.setFullYear(nd.getFullYear() + y)
          return nd
        }
        const addMonths = (d: Date, m: number) => {
          const nd = new Date(d)
          nd.setMonth(nd.getMonth() + m)
          return nd
        }

        let years = 0
        while (addYears(cursor, 1) <= targetDate) {
          cursor = addYears(cursor, 1)
          years++
        }
        let months = 0
        while (addMonths(cursor, 1) <= targetDate) {
          cursor = addMonths(cursor, 1)
          months++
        }

        let remainderMs = targetDate.getTime() - cursor.getTime()
        if (remainderMs < 0) remainderMs = 0
        const remSeconds = Math.floor(remainderMs / 1000)
        const days = Math.floor(remSeconds / 86400)
        const hours = Math.floor((remSeconds % 86400) / 3600)
        const minutes = Math.floor((remSeconds % 3600) / 60)
        const seconds = remSeconds % 60

        const units: string[] = []
        if (years > 0) units.push(labels.format(years, "year", intFmt))
        if (months > 0) units.push(labels.format(months, "month", intFmt))
        if (days > 0) units.push(labels.format(days, "day", intFmt))

        setUnitsDisplay(units.join(" "))
        setClockDisplay(
          `${twoDigits.format(hours)}:${twoDigits.format(minutes)}:${twoDigits.format(seconds)}`,
        )
      } else if (showDays) {
        const days = Math.floor(totalSeconds / 86400)
        const hours = Math.floor((totalSeconds % 86400) / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60

        setUnitsDisplay(days > 0 ? labels.format(days, "day", intFmt) : "")
        setClockDisplay(
          `${twoDigits.format(hours)}:${twoDigits.format(minutes)}:${twoDigits.format(seconds)}`,
        )
      } else {
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60

        setUnitsDisplay("")
        setClockDisplay(
          `${twoDigits.format(hours)}:${twoDigits.format(minutes)}:${twoDigits.format(seconds)}`,
        )
      }
    }

    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [targetMs, twoDigits, intFmt, showDays, showMonthsYears, onEnd, labels])

  return (
    <StyledStrong className={className}>
      {unitsDisplay && <UnitsDisplay>{unitsDisplay} </UnitsDisplay>}
      <ClockDisplay>{clockDisplay}</ClockDisplay>
    </StyledStrong>
  )
}
